import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/base/VideoCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, categorySlugMap } from '@/mocks/categories';
import { getVideosByCategory } from '@/mocks/videos';
import type { Video } from '@/mocks/videos';

export default function CategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [activeSlug, setActiveSlug] = useState(categorySlug || 'dianying');
  const [videos, setVideos] = useState<Video[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'hot' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug, sortBy]);

  useEffect(() => {
    if (categorySlug) {
      setActiveSlug(categorySlug);
    }
  }, [categorySlug]);

  useEffect(() => {
    let result = getVideosByCategory(activeSlug);
    if (sortBy === 'hot') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    setVideos(result);
  }, [activeSlug, sortBy]);

  const categoryName = categorySlugMap[activeSlug] || activeSlug;
  const categoryInfo = categories.find((c) => c.slug === activeSlug);

  const handleCategoryChange = (slug: string) => {
    setActiveSlug(slug);
    window.history.pushState(null, '', `/category/${slug}`);
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 久久在线观看高清视频`,
    description: categoryInfo?.description || `${categoryName}分类下的所有高清视频资源`,
    url: `https://mobiang.cn/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '久久在线观看',
    },
  };

  const sortOptions = [
    { value: 'latest', label: '最新' },
    { value: 'hot', label: '最热' },
    { value: 'rating', label: '评分' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName },
              ]}
            />

            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {categoryName}
              </h1>
              {categoryInfo && (
                <p className="text-sm text-foreground-500 mt-2">{categoryInfo.description}</p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="flex items-center gap-1.5 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1.5 ml-auto bg-background-100 rounded-full px-1 py-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value as 'latest' | 'hot' | 'rating')}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {videos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {videos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-video-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无视频资源</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}